import React from 'react'
import axios from 'axios'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  Grid, Chip, CircularProgress, Divider, Collapse, IconButton,
  FormControlLabel, Checkbox, TextField, Link as MuiLink
} from '@material-ui/core'
import {
  Close as CloseIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon, Cancel as CancelIcon, GetApp as DownloadIcon
} from '@material-ui/icons'

const formatBytes = (bytes) => {
  if (bytes == null) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`
}

const OUTCOME_CHIP = {
  parsed: { label: 'Parsed', style: { backgroundColor: '#c8e6c9', color: '#1b5e20' } },
  parsed_with_warnings: { label: 'Parsed with warnings', style: { backgroundColor: '#ffe0b2', color: '#e65100' } },
  empty_result: { label: 'Empty result', style: { backgroundColor: '#eeeeee', color: '#424242' } },
  parse_error: { label: 'Parse error', style: { backgroundColor: '#ffcdd2', color: '#b71c1c' } },
  exception: { label: 'Exception', style: { backgroundColor: '#ffcdd2', color: '#b71c1c' } },
  unsupported_format: { label: 'Unsupported format', style: { backgroundColor: '#eeeeee', color: '#424242' } }
}
const outcomeChipProps = (o) => OUTCOME_CHIP[o] || { label: o || 'unknown', style: { backgroundColor: '#eeeeee', color: '#424242' } }

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 20 }}>
    <Typography variant="subtitle2" style={{ textTransform: 'uppercase', letterSpacing: 0.5, color: '#666', marginBottom: 8 }}>
      {title}
    </Typography>
    {children}
  </div>
)

const KV = ({ k, v, mono }) => (
  <Grid container spacing={1} style={{ marginBottom: 4 }}>
    <Grid item xs={4}><Typography variant="body2" color="textSecondary">{k}</Typography></Grid>
    <Grid item xs={8}>
      <Typography
        variant="body2"
        style={mono
          ? { fontFamily: 'Menlo, Monaco, "Courier New", monospace', wordBreak: 'break-all' }
          : { wordBreak: 'break-word' }}
      >
        {v != null && v !== '' ? v : <span style={{ color: '#aaa' }}>—</span>}
      </Typography>
    </Grid>
  </Grid>
)

const CollapsibleBlock = ({ title, content }) => {
  const [open, setOpen] = React.useState(false)
  if (!content) return null
  return (
    <div style={{ marginBottom: 8 }}>
      <Button size="small" onClick={() => setOpen(!open)} startIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
        {title}
      </Button>
      <Collapse in={open}>
        <pre style={{
          fontFamily: 'Menlo, Monaco, "Courier New", monospace', fontSize: 11,
          background: '#272822', color: '#f8f8f2', padding: 8, borderRadius: 4,
          maxHeight: 240, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: '4px 0 0 0'
        }}>
          {content}
        </pre>
      </Collapse>
    </div>
  )
}

const FileSection = ({ file, createdAt }) => (
  <Section title="File">
    <KV k="Filename" v={file.originalFilename} />
    <KV k="Size" v={formatBytes(file.sizeBytes)} />
    <KV k="Extension" v={file.extension} />
    <KV k="MIME type" v={file.mimeType} mono />
    <KV k="Hash" v={file.fileHash ? `${file.fileHash.slice(0, 16)}...` : null} mono />
    <KV k="Uploaded" v={createdAt ? new Date(createdAt).toLocaleString() : null} />
    {file.s3Url && (
      <div style={{ marginTop: 8 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={() => window.open(file.s3Url, '_blank', 'noopener,noreferrer')}
        >
          Download File
        </Button>
      </div>
    )}
  </Section>
)

const ParserSection = ({ parser, detail }) => (
  <Section title="Parser">
    <KV k="Name" v={parser.name} mono />
    <KV k="Entry point" v={parser.entryPoint} mono />
    <KV k="Version" v={parser.version} />
    <KV k="Elapsed" v={detail.elapsedMs != null ? `${detail.elapsedMs} ms` : null} />
    {detail.pageCount != null && <KV k="Pages" v={String(detail.pageCount)} />}
    {detail.sheetCount != null && <KV k="Sheets" v={String(detail.sheetCount)} />}
  </Section>
)

const OutcomeSection = ({ detail }) => (
  <Section title="Outcome">
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
      {detail.success
        ? <CheckCircleIcon style={{ color: '#2e7d32' }} />
        : <CancelIcon style={{ color: '#c62828' }} />}
      <Chip size="small" {...outcomeChipProps(detail.outcome)} />
    </div>
    <KV k="Groups found" v={String(detail.groupsFound != null ? detail.groupsFound : 0)} />
    <KV k="Items found" v={String(detail.itemsFound != null ? detail.itemsFound : 0)} />
  </Section>
)

const ErrorSection = ({ error }) => (
  <Section title="Error">
    {error.type && <KV k="Type" v={error.type} mono />}
    {error.pythonException && <KV k="Python exception" v={error.pythonException} mono />}
    {error.message && (
      <div style={{ marginBottom: 8 }}>
        <Typography variant="caption" color="textSecondary">Message</Typography>
        <Typography variant="body2" style={{
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          fontFamily: 'Menlo, Monaco, "Courier New", monospace',
          background: '#fff3e0', padding: 8, borderRadius: 4, marginTop: 4
        }}>
          {error.message}
        </Typography>
      </div>
    )}
    <CollapsibleBlock title="Stdout" content={error.stdout} />
    <CollapsibleBlock title="Stderr" content={error.stderr} />
    <CollapsibleBlock title="Stack trace" content={error.stack} />
  </Section>
)

const ContextSection = ({ detail }) => (
  <Section title="Context">
    <KV k="User" v={detail.user && (detail.user.name || detail.user.username)} />
    <Grid container spacing={1} style={{ marginBottom: 4 }}>
      <Grid item xs={4}><Typography variant="body2" color="textSecondary">Project</Typography></Grid>
      <Grid item xs={8}>
        {detail.project ? (
          <MuiLink href={`/bid-estimation/${detail.project._id}`} target="_blank" rel="noopener noreferrer">
            {detail.project.name || detail.project._id}
          </MuiLink>
        ) : <Typography variant="body2" style={{ color: '#aaa' }}>—</Typography>}
      </Grid>
    </Grid>
    <Grid container spacing={1} style={{ marginBottom: 4 }}>
      <Grid item xs={4}><Typography variant="body2" color="textSecondary">Bid Package</Typography></Grid>
      <Grid item xs={8}>
        {detail.bidPackage && detail.project ? (
          <MuiLink
            href={`/bid-estimation/${detail.project._id}/bid-response/${detail.bidPackage._id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {detail.bidPackage.name || detail.bidPackage._id}
          </MuiLink>
        ) : detail.bidPackage
          ? <Typography variant="body2">{detail.bidPackage.name || detail.bidPackage._id}</Typography>
          : <Typography variant="body2" style={{ color: '#aaa' }}>—</Typography>}
      </Grid>
    </Grid>
    <KV k="Created" v={detail.createdAt ? new Date(detail.createdAt).toLocaleString() : null} />
  </Section>
)

const ParseLogDetailDialog = ({ open, onClose, attemptId, url, onUpdate }) => {
  const [detail, setDetail] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [notes, setNotes] = React.useState('')
  const [confirmDelete, setConfirmDelete] = React.useState(false)

  React.useEffect(() => {
    if (open && attemptId) {
      setLoading(true)
      axios.get(`${url}/api/bid-estimation/parse-log/${attemptId}`)
        .then(res => { setDetail(res.data); setNotes(res.data.notes || ''); setLoading(false) })
        .catch(err => { console.error('Failed to load parse log detail', err); setLoading(false) })
    } else {
      setDetail(null)
      setConfirmDelete(false)
    }
  }, [open, attemptId, url])

  const handlePatch = (body) => {
    setSaving(true)
    axios.put(`${url}/api/bid-estimation/parse-log/${attemptId}`, body)
      .then(res => { setDetail(res.data); setSaving(false); if (onUpdate) onUpdate() })
      .catch(err => { console.error('Failed to update parse log', err); setSaving(false) })
  }

  const handleDelete = () => {
    if (!confirmDelete) { setConfirmDelete(true); return }
    axios.delete(`${url}/api/bid-estimation/parse-log/${attemptId}`)
      .then(() => { if (onUpdate) onUpdate(); onClose() })
      .catch(err => console.error('Failed to delete parse log', err))
  }

  const file = (detail && detail.file) || {}
  const parser = (detail && detail.parser) || {}
  const error = (detail && detail.error) || {}
  const warnings = (detail && detail.warnings) || []
  const hasError = detail && !detail.success &&
    (error.type || error.message || error.stdout || error.stderr || error.stack)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Parse Attempt Detail</Typography>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </div>
      </DialogTitle>

      <DialogContent dividers style={{ minHeight: 400 }}>
        {loading && <div style={{ textAlign: 'center', padding: 32 }}><CircularProgress /></div>}
        {!loading && detail && (
          <>
            <FileSection file={file} createdAt={detail.createdAt} />
            <Divider style={{ margin: '12px 0' }} />
            <ParserSection parser={parser} detail={detail} />
            <Divider style={{ margin: '12px 0' }} />
            <OutcomeSection detail={detail} />

            {hasError && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <ErrorSection error={error} />
              </>
            )}

            {warnings.length > 0 && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <Section title={`Warnings (${warnings.length})`}>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {warnings.map((w, i) => (
                      <li key={i}><Typography variant="body2" style={{ color: '#e65100' }}>{w}</Typography></li>
                    ))}
                  </ul>
                </Section>
              </>
            )}

            <Divider style={{ margin: '12px 0' }} />
            <Section title="Admin Flags">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!detail.reviewed}
                    disabled={saving}
                    onChange={(e) => handlePatch({ reviewed: e.target.checked })}
                    color="primary"
                  />
                }
                label="Reviewed"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!detail.resolved}
                    disabled={saving}
                    onChange={(e) => handlePatch({ resolved: e.target.checked })}
                    color="primary"
                  />
                }
                label="Resolved"
              />
              <TextField
                label="Notes"
                multiline
                minRows={2}
                fullWidth
                variant="outlined"
                size="small"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={() => { if (notes !== (detail.notes || '')) handlePatch({ notes }) }}
                style={{ marginTop: 8 }}
              />
            </Section>

            <Divider style={{ margin: '12px 0' }} />
            <ContextSection detail={detail} />
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleDelete}
          style={{
            color: confirmDelete ? '#fff' : '#c62828',
            backgroundColor: confirmDelete ? '#c62828' : 'transparent'
          }}
        >
          {confirmDelete ? 'Click again to confirm delete' : 'Delete'}
        </Button>
        <div style={{ flex: 1 }} />
        <Button onClick={onClose} variant="contained" color="primary">Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default ParseLogDetailDialog
